import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UrlService } from '@service/url/url.service';
import { ADD_DAYS, DATE_TIME, DATE_YMD, Owner, ProjectDTO, RestService } from 'cargo-chief-commons';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';

@Component({
	selector: 'app-task',
	templateUrl: './task.component.html',
	styleUrls: ['./task.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class TaskComponent implements OnInit {
	@Input() editTask: boolean = false;
	@Input() addTask: boolean = false;
	@Input() displayAddButton: boolean = false;
	@Input() projectDTO: ProjectDTO = null;
	@Input() carrierDTO: any = null;
	@Input() displayInputText: boolean = false;
	@Input() displayAddLink: boolean = false;
	@Input() userAssignedToCarrier: number = null;

	@Output() newAddedTask = new EventEmitter<string>();

	constructor(public dialog: MatDialog, public utilService: UtilService) {}

	ngOnInit() {}

	openTaskDialog(): void {
		const dialogRef = this.dialog.open(TaskDialog, {
			width: '600px',
			data: {
				editTask: this.editTask,
				addTask: this.addTask,
				projectDTO: this.projectDTO,
				carrierDTO: this.carrierDTO,
				userAssignedToCarrier: this.userAssignedToCarrier,
			},
			disableClose: true,
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result)) {
				if (this.addTask) {
					this.newAddedTask.emit(result);
				}
			}
		});
	}
}

@Component({
	selector: 'app-task-dialog',
	templateUrl: 'modal.html',
})
export class TaskDialog implements OnInit {
	taskDetails: UntypedFormGroup;
	isFormInitialized: boolean = false;
	isRequestInProgress: boolean = false;
	accountId = null;

	dueDate = new UntypedFormControl(new Date(ADD_DAYS(DATE_TIME(new Date()), 1)));
	minimumDueDate = new Date();
	assignee: Owner = null;
	isAdminOrOwner: boolean = false;
	userId;
	selectedUsers = [];

	constructor(
		public dialogRef: MatDialogRef<TaskDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private formBuilder: UntypedFormBuilder,
		private cargochief: Cargochief,
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private segmentWrapper: SegmentWrapperService
	) {}

	ngOnInit() {
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		this.userId = this.cargochief.USER_ID;
		this.accountId = this.cargochief.ACCOUNT_ID;
		this.setUpForm();
		this.isFormInitialized = true;
	}

	setUpForm() {
		this.taskDetails = this.formBuilder.group({
			title: ['', Validators.required],
			project: {
				id: !this.utilService.isNullOrUndefined(this.data.projectDTO)
					? this.data.projectDTO.id
					: null,
			},
			carrier: {
				id:
					!this.utilService.isNullOrUndefined(this.data.carrierDTO) &&
					!this.utilService.isNullOrUndefined(this.data.carrierDTO.id)
						? this.data.carrierDTO.id
						: null,
			},
			dueDate: null,
			assigneeId: [!this.isAdminOrOwner ? this.userId : null, Validators.required],
			userId: this.userId,
			accountId: this.accountId,
			deleted: false,
		});
		if (this.isAdminOrOwner) {
			let assigneeId: number = null;
			if (
				!this.utilService.isNullOrUndefined(this.data.projectDTO) &&
				!this.utilService.isNullOrUndefined(this.data.projectDTO.assigneeId)
			) {
				assigneeId = this.data.projectDTO.assigneeId;
			} else if (!this.utilService.isNullOrUndefined(this.data.userAssignedToCarrier)) {
				assigneeId = this.data.userAssignedToCarrier;
			}
			if (!this.utilService.isNullOrUndefined(assigneeId)) {
				this.selectedUsers.length = 0;
				this.selectedUsers.push(assigneeId);
				this.taskDetails.get('assigneeId').setValue(assigneeId);
			}
		}
	}

	setProjectId(project: ProjectDTO) {
		this.taskDetails.patchValue({
			project: { id: !this.utilService.isNullOrUndefined(project) ? project.id : null },
		});
	}

	setAssigneeId(user: Owner) {
		this.assignee = user;
		this.taskDetails
			.get('assigneeId')
			.setValue(!this.utilService.isNullOrUndefined(user) ? user.userId : null);
	}

	setCarrierId(carrier: any) {
		this.taskDetails.patchValue({
			carrier: { id: !this.utilService.isNullOrUndefined(carrier) ? carrier.id : null },
		});
	}

	addTask() {
		if (this.taskDetails.valid) {
			this.isRequestInProgress = true;
			this.taskDetails.get('dueDate').setValue(DATE_YMD(this.dueDate.value));
			const url = this.urlService.addTasks();
			let payload = Object.assign({}, this.taskDetails.value);
			if (this.utilService.isNullOrUndefined(payload.carrier.id)) {
				delete payload.carrier;
			}
			if (this.utilService.isNullOrUndefined(payload.project.id)) {
				delete payload.project;
			}
			this.fireSegmentEvent(payload);
			this.restService.POST(url, payload).subscribe(
				(result: any) => {
					this.utilService.showSnackbar(
						'Task added successfully.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					if (!this.utilService.isNullOrUndefined(this.assignee)) {
						result.data['assigneeName'] = this.assignee.name;
					}
					this.closeDialog(result.data);
					this.isRequestInProgress = false;
				},
				(error) => {
					this.utilService.showSnackbar('Unable to add Task now.', SNACKBAR.ERROR, SNACKBAR.ACTION);
					this.isRequestInProgress = false;
				}
			);
		}
	}

	fireSegmentEvent(task: any) {
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
			assignee: this.assignee,
			task: task,
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_created_task, payload);
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}
}
